import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal, Slider, TextField, Typography } from "@mui/material";
import Map, { Layer, LayerProps, LngLatLike, MapLayerMouseEvent, Marker, Popup, Source } from 'react-map-gl';
import DeleteIcon from '@mui/icons-material/Delete';
import { deletePin, getPins, Pin, Place, postPin, searchSpots } from "../api";
import { useNavigate } from "react-router-dom";
import 'mapbox-gl/dist/mapbox-gl.css';
import { PinMarker } from "./PinMarker";
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { PlaceMarker } from "./PlaceMarker";
import * as turf from '@turf/turf';
import { Check } from "@mui/icons-material";

const mapboxglAccessToken = 'pk.eyJ1IjoibWlrZXJqYWNvYmkiLCJhIjoiY201MWtldDVkMXBubDJzcTdjZXk3N2R2ZiJ9.yBuunZGJC5p5VfMRrcQPGA';
const mapboxglMapStyle = 'mapbox://styles/mapbox/light-v11';
const mapboxglMapCenter = [-122.335167, 47.708013];
const mapboxglMapZoom = 12;

interface AddPinPopupProps {
  onClose: () => void;
  onSubmit: (e: MapLayerMouseEvent, label: string) => void;
  lng: number;
  lat: number;
}

const AddPinPopup = ({ onClose, onSubmit, lng, lat }: AddPinPopupProps) => {
  const [currentLabel, setCurrentLabel] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSubmit({ lngLat: { lng, lat } }, currentLabel);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Popup
      longitude={lng}
      latitude={lat}
      anchor="left"
      onClose={onClose}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <TextField
          label="Pin Name"
          value={currentLabel}
          onChange={(e) => setCurrentLabel(e.target.value)}
          onKeyDown={handleKeyDown} // Use onKeyDown instead of onKeyPress
        />
        <IconButton
          color="primary"
          onClick={() => onSubmit({ lngLat: { lng, lat } }, currentLabel)}
        >
          <Check />
        </IconButton>
      </Box>
    </Popup>
  );
};

// Style for the line
const lineLayer: LayerProps = {
  id: 'line-layer',
  type: 'line',
  paint: {
    'line-color': '#0000FF',
    'line-width': 1,
  },
};

const MapComponent = ({ token }: { token: string }) => {
  const [viewState, setViewState] = useState({
    longitude: mapboxglMapCenter[0],
    latitude: mapboxglMapCenter[1],
    zoom: mapboxglMapZoom,
  });
  const [address, setAddress] = useState("");
  const [pins, setPins] = useState<Pin[]>([]);
  const [lineGeoJson, setLineGeoJson] = useState<GeoJSON.LineString | null>(null);
  const [center, setCenter] = useState<GeoJSON.Point | null>(null);
  const [searchRadiusMiles, setSearchRadiusMiles] = useState(0.3);
  const [searchAreaGeoJson, setSearchAreaGeoJson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [pinPopupLoc, setPinPopupLoc] = useState<LngLatLike | null>(null);
  const navigate = useNavigate();

  const loadPins = async () => {
    const pins = await getPins(token);
    setPins(pins);
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    loadPins();
  }, []);

  const handleSearch = async () => {
    try {
      const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?access_token=${mapboxglAccessToken}`);
      const data = await response.json();

      const feature = data.features[0];
      if (!feature) {
        console.error("No features found for this address");
        return;
      }

      const center = feature.center;
      const placeType = feature.place_type[0];

      let zoom = 14;
      switch (placeType) {
        case "country": zoom = 4; break;
        case "region": zoom = 6; break;
        case "place": zoom = 10; break;
        case "address": zoom = 14; break;
        case "poi": zoom = 16; break;
        default: zoom = 12; break;
      }

      setViewState({
        longitude: center[0],
        latitude: center[1],
        zoom,
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleAddPin = async () => {
    // Handle pin addition logic
  };

  const handleRemovePin = async (id: string) => {
    await deletePin(id, token);
    const updatedPins = pins.filter((pin) => pin.id !== id);
    setPins(updatedPins);
    handleFindSpots(updatedPins);
  };

  const handleMapClick = async (e: MapLayerMouseEvent) => {
    setPinPopupLoc(e.lngLat);
  };

  const handlePinSubmit = async (e: MapLayerMouseEvent, label: string) => {
    const pinReq = {
      center: [e.lngLat.lng, e.lngLat.lat],
      label,
    };
    try {
      const pin = await postPin(pinReq, token);
      const updatedPins = [...pins, pin];
      setPins(updatedPins);
      handleFindSpots(updatedPins);
      setPinPopupLoc(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleFindSpots = async (pins: Pin[]) => {
    const spotResponse = await searchSpots({ pins, searchRadiusMiles }, token);
    console.log(spotResponse);
    setLineGeoJson(spotResponse.geojson);
    setCenter(spotResponse.center);
    setPlaces(spotResponse.places);
    const circle = turf.circle(spotResponse.center.coordinates, searchRadiusMiles, {
      units: "miles",
    });
    setSearchAreaGeoJson({
      type: "FeatureCollection",
      features: [circle],
    });

  };


  const handleCenterView = () => {
    if (center) {
      setViewState({
        longitude: center.coordinates[0],
        latitude: center.coordinates[1],
        zoom: 14,
      });
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100vw", height: "100vh" }}>
      <Map
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        mapboxAccessToken={mapboxglAccessToken}
        mapStyle={mapboxglMapStyle}
        onClick={handleMapClick}
        style={{ width: "100%", height: "100%" }}
      >
        {pins.map((pin) => (
          <PinMarker key={pin.id} pin={pin} deletePin={handleRemovePin} />
        ))}
        {places.map((place) => (
          <PlaceMarker key={place.name} place={place} />
        ))}
        {lineGeoJson && (
          <Source id="line-source" type="geojson" data={lineGeoJson}>
            <Layer {...lineLayer} />
          </Source>
        )}
        {pinPopupLoc && (
          <AddPinPopup
            key={pinPopupLoc.toString()}
            onClose={() => setPinPopupLoc(null)}
            onSubmit={handlePinSubmit}
            lng={pinPopupLoc.lng}
            lat={pinPopupLoc.lat}
          />
        )}
        {center && (
          <>
            <Marker longitude={center.coordinates[0]} latitude={center.coordinates[1]}>
              <CenterFocusStrongIcon />
            </Marker>
            <Source id="circle-source" type="geojson" data={searchAreaGeoJson}>
              <Layer
                id="circle-layer"
                type="fill"
                paint={{
                  "fill-color": "rgba(255, 0, 0, 0.5)", // Transparent red
                  "fill-opacity": 0.5,
                }}
              />
            </Source>
          </>
        )}
      </Map>
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          zIndex: 10,
          backgroundColor: "white",
          padding: 2,
          borderRadius: 2,
          opacity: 0.8,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            variant="outlined"
            sx={{ width: "300px" }}
          />
          <Button variant="contained" color="primary" onClick={handleSearch}>
            Search
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography>Search Radius: {searchRadiusMiles.toFixed(1)} miles</Typography>
          <Slider
            value={searchRadiusMiles}
            onChange={(e, value) => setSearchRadiusMiles(value as number)}
            min={0.1}
            max={1.0}
            step={0.1}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" color="primary" onClick={() => handleFindSpots(pins)}>
            Find Spots
          </Button>
          <Button variant="contained" color="primary" onClick={handleCenterView}>
            Center View
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MapComponent;